import { CAMPAIGNS_API } from 'modules/campaigns/api/constants'
import {
  CampaignStepCreateApiInterface,
  CampaignStepId,
  CampaignStepInterface,
  MailingDeliveryStatsApiInterface,
} from 'modules/campaigns/campaign-steps/types/campaign-step-interface'
import { CampaignId } from 'modules/campaigns/types/campaign-interface'
import { Dispatch, SetStateAction } from 'react'
import httpClient from 'shared/utils/http-client'
import { encodeArrayQueryParam } from '../utils/encode-array-query-param'

export const removeCampaignStep = (id: CampaignStepId) =>
  httpClient.delete(`${CAMPAIGNS_API}/steps/${id}`)

export const createCampaignStep = (id: CampaignId, data: CampaignStepCreateApiInterface) =>
  httpClient.post<CampaignStepInterface>(`${CAMPAIGNS_API}/${id}/steps`, data)

export const duplicateCampaignStep = (id: CampaignStepId) =>
  httpClient.post<CampaignStepInterface>(`${CAMPAIGNS_API}/duplicate/steps/${id}`)

export const deactivateCampaignStep = (id: CampaignStepId) =>
  httpClient.post<string>(`${CAMPAIGNS_API}/deactivate/steps/${id}`)

export const editNoteCampaignStep = (
  id: CampaignStepId,
  data: Pick<CampaignStepInterface, 'note'>,
) => httpClient.post<string>(`${CAMPAIGNS_API}/edit-note/steps/${id}`, data)

export const getCampaignStepsStatistics = (
  campaignId: CampaignId,
  campaignStepIds: CampaignStepId[],
) =>
  httpClient.get<MailingDeliveryStatsApiInterface>(
    `${CAMPAIGNS_API}/${campaignId}/delivery-statistic?${encodeArrayQueryParam(
      'steps',
      campaignStepIds,
    )}`,
  )

export const getChunkedCampaignStepsStatistics = async (
  campaignId: CampaignId,
  campaignStepIds: CampaignStepId[],
  step: number,
  update: Dispatch<SetStateAction<MailingDeliveryStatsApiInterface | undefined>>,
  retryCount: number = 0,
) => {
  if (retryCount > 1) return

  const countChunks = Math.ceil(campaignStepIds.length / step)

  // inspiration: https://stackoverflow.com/a/49499491
  // the flow is like this:
  // each step of the reduce function will return a promise
  await Array.from({ length: countChunks }).reduce(async (promise, _, page) => {
    // first, await the previous promise so the current call will happen only after it
    await promise
    const startFrom = page * step
    const currentChunk = campaignStepIds.slice(startFrom, startFrom + step)

    try {
      const { data } = await getCampaignStepsStatistics(campaignId, currentChunk)
      update(prev => ({ ...prev, ...data }))
    } catch {
      await getChunkedCampaignStepsStatistics(campaignId, currentChunk, 1, update, retryCount + 1)
    }
    // use promise as accumulator for the start of the chain
  }, Promise.resolve())
}
